<template>
  <div
    :class="[$style.container, global && $style.global, {[$style.isScrolled]: isScrolled}]"
    @click="scrollToTop"
  >
    <div :class="$style.content">
      <svg
        :class="[$style.circle, $style.fade]"
        width="60"
        height="60"
        viewBox="0 0 60 60"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle :class="$style.bar" r="30" cx="30" cy="30"></circle>
        <circle
          v-show="circumference > 0"
          :class="$style.progress"
          :stroke-dasharray="circumference"
          :stroke-dashoffset="dashoffset"
          :style="{ 'transition-duration': isFullPageMode ? '0.5s' : '0.01s' }"
          ref="progressElement"
          r="30"
          cx="30"
          cy="30"
          transform="rotate(-90 30 30)"
        ></circle>
      </svg>

      <svg
        width="20"
        height="24"
        viewBox="0 0 20 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        :class="$style.arrow"
        :style="[dashoffset <= 0 && { transform: 'rotate(180deg)' }]"
      >
        <path
          d="M0.25 15L10 23.5L19.75 15L18.5 13.75L10 21.15L1.5 13.75L0.25 15Z"
          fill="black"
        />
        <path
          d="M9.25 0V20.5L10 21.15L10.75 20.5V0H9.25Z"
          fill="black"
          :class="$style.fade"
        />
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ScrollIndicator',

  props: {
    global: {
      type: Boolean,
      default: false
    },

    isScrolled: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    circumference: 0,
    scrollingPogress: 0,
    tick: false,
    windowScroll: 0,
    viewport: 0,
    wrapperHeight: 0,
    unwatchFP: null
  }),

  computed: {
    dashoffset () {
      const progressPercent = this.scrollingPogress
      let dashoffset = this.circumference
      if (progressPercent >= 1) {
        dashoffset = 0
      } else if (progressPercent >= 0) {
        dashoffset = this.circumference * (1 - progressPercent)
      }
      return dashoffset
    },

    isFullPageMode () {
      let fullPageMain = this.$store.getters['fullpage/isActive']
      let fullPageCard = this.$store.getters['fpcard/isActive']
      let fullPage = fullPageMain || fullPageCard
      return fullPage
    },

    fullPageTotalSections () {
      let fullPageMain = this.$store.getters['fullpage/isActive']
      let totalSections
      fullPageMain
        ? (totalSections = this.$store.getters['fullpage/totalPages'] - 1)
        : (totalSections = this.$store.getters['fpcard/totalPages'] - 1)
      return totalSections
    },

    fullPageProgress () {
      let fullPageMain = this.$store.getters['fullpage/isActive']
      let pageProgress
      fullPageMain
        ? (pageProgress = this.$store.getters['fullpage/activePage'])
        : (pageProgress = this.$store.getters['fpcard/activePage'])
      return pageProgress
    }
  },

  methods: {
    build () {
      this.refresh()
      this.update()
      this.toggleMode()
    },

    toggleMode () {
      this.destroy()
      if (this.isFullPageMode === true) {
        this.unwatchFP = this.$watch(
          (vm) => [vm.fullPageProgress, vm.fullPageTotalSections].join(),
          this.update
        )
      } else {
        window.addEventListener('scroll', this.scrollHandler)
        window.addEventListener('resize', this.refresh)
      }
    },

    destroy () {
      this.scrollingPogress = 0
      this.unwatchFP instanceof Function && this.unwatchFP()
      window.removeEventListener('scroll', this.scrollHandler)
      window.removeEventListener('resize', this.refresh)
    },

    refresh () {
      window.requestAnimationFrame(() => {
        const radius =
          Number(this.$refs.progressElement.getAttribute('r')) || 0
        this.circumference = 2 * Math.PI * radius
        this.wrapperHeight = document.documentElement.scrollHeight
        this.viewport = window.innerHeight
      })
    },

    scrollHandler () {
      if (this.tick === false) {
        window.requestAnimationFrame(() => {
          this.windowScroll = document.documentElement.scrollTop
          this.wrapperHeight = document.documentElement.scrollHeight
          this.viewport = window.innerHeight
          this.tick = false
          this.update()
        })
        this.tick = true
      }
    },

    update () {
      this.scrollingPogress = this.isFullPageMode
        ? this.fullPageProgress / this.fullPageTotalSections
        : this.windowScroll / (this.wrapperHeight - this.viewport)
    },

    scrollToTop () {
      if (this.isFullPageMode === true) {
        this.$store.dispatch('fullpage/scrollToTop')
        this.$store.dispatch('fpcard/scrollToTop')
      } else {
        window.scroll({ top: 0, left: 0, behavior: 'smooth' })
      }
    }
  },

  watch: {
    isFullPageMode () {
      this.toggleMode()
    }
  },

  mounted () {
    this.build()
  },

  beforeDestroy () {
    this.destroy()
  }
}
</script>

<style module lang="scss">

.container {
  position: absolute;
  width: 70px;
  height: 70px;
  bottom: vw(50);
  left: 0;
  right: 0;
  margin: 0 auto;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  color: #000;
  @include max-width(xlg) {
    width: 64px;
    height: 64px;
  }
  @include max-width(lg) {
    width: 54px;
    height: 54px;
  }
  @include max-width(md-lg) {
    width: 44px;
    height: 44px;
  }
  @include max-width(notebook) {
    width: 42px;
    height: 42px;
  }
  @include max-width(md-old) {
    width: 36px;
    height: 36px;
  }
  @include max-width(tablet) {
    width: 30px;
    height: 30px;
  }
  @include max-width(mobile) {
    bottom: vw-m(30);
  }
}
.global {
  z-index: 10;
  width: 60px;
  height: 60px;
  bottom: vw(40);
  position: fixed;
  mix-blend-mode: exclusion;
  @include max-width(md-lg) {
    width: 50px;
    height: 50px;
  }
  @include max-width(notebook) {
    width: 48px;
    height: 48px;
  }
  @include max-width(md-old) {
    width: 42px;
    height: 42px;
  }
  @include max-width(tablet) {
    width: 36px;
    height: 36px;
  }
  @include max-width(mobile) {
    mix-blend-mode: normal;
    display: none;
  }
  @include max-width(xs) {
    bottom: 0;
  }
}
.content {
  position: relative;
  width: 100%;
  height: 100%;
}
.circle {
  fill: none;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}
.bar {
  stroke: #ccc;
  stroke-width: 3px;
  @at-root .global & {
    stroke: rgba(#dfdfdf, 0.25);
  }
}
.progress {
  stroke: #000;
  stroke-width: 3px;
  transition: stroke-dashoffset 0.5s $cubic-basic 0s;
  @at-root .global & {
    stroke: #fff;
  }
}
.arrow {
  fill: currentcolor;
  width: vw(20);
  height: vw(24);
  stroke: none;
  position: absolute;
  top: 0;
  left: vw(2);
  right: 0;
  bottom: 0;
  margin: auto;
  transform: rotate(0);
  transition: transform 0.25s $cubic-basic 0s;
  animation: jump 3s infinite;
  @at-root .container:hover & {
    transform: rotate(180deg);
  }
  @at-root .global & {
    fill: #fff;
    left: 0;
  }
}
.fade {
  opacity: 0;
  transition: opacity 0.25s ease 0s;
}
.isScrolled {
  .arrow {
    animation: none;
  }
  .fade {
    // animation: fade-in 0.25s forwards;
    opacity: 1;
  }
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }
  80% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@keyframes jump {
  0% {
    top: 0px;
  }
  20% {
    top: 5px;
  }
  50% {
    top: -16px;
  }
  75% {
    top: 0px;
  }
  90% {
    top: 0px;
  }
  100% {
    top: 0;
  }
}
</style>
